import { IconButton, useElevation } from '@getsynapse/design-system';
import { usePopper } from 'react-popper';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import intl from 'react-intl-universal';
import NewComment from './NewComment/NewComment';
import CommentsList from './CommentsList';
import {
  QuestionCommentType,
  ProjectCommentMentionsData,
} from 'utils/customTypes';
import { selectActiveRequestMentionableUsers } from 'state/ActiveRequest/activeRequestSlice';

const Comments = ({
  comments = [],
  className = '',
  onEdit = () => {},
  onDelete = () => {},
  onCreate = () => {},
  testId,
  isPopupOpen = false,
}: {
  comments: QuestionCommentType[];
  className?: string;
  testId?: string;
  onEdit?: ({
    commentId,
    content,
    mentionsData,
  }: {
    commentId: string;
    content: string;
    mentionsData: ProjectCommentMentionsData;
  }) => void;
  onDelete?: (commentId: string) => void;
  onCreate?: (
    content: string,
    mentionsData: ProjectCommentMentionsData
  ) => void;
  isPopupOpen?: boolean;
}) => {
  const overflowMenuOptions = useMemo<string[]>(
    () => [intl.get('COMMENT.EDIT'), intl.get('COMMENT.DELETE')],
    []
  );
  const mentionableUsers = useSelector(selectActiveRequestMentionableUsers);
  const element = document.querySelector('#request-tabs-container');
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [isPopupUpdated, setIsPopupUpdated] = useState<boolean>(false);
  const buttonElement = useRef<HTMLDivElement>(null);
  const popperElement = useRef<HTMLDivElement>(null);
  const prevCommentsLength = useRef<number>(comments.length);

  const popperElevation = useElevation(2);
  const customModifier = useMemo(
    () => ({
      name: 'flip',
      options: {
        fallbackPlacements: ['top', 'right'],
        boundary: element,
      },
    }),
    [element]
  );
  const {
    styles,
    attributes,
    update: UpdatePopup,
  } = usePopper(buttonElement.current, popperElement.current, {
    placement: 'auto-start',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: [0, 4],
        },
      },
      customModifier,
    ],
  });

  const handleClickOutside = useCallback(
    (event: Event) => {
      const target = event.target as Element;
      if (
        !overflowMenuOptions.includes(
          target.getAttribute('data-valuetext') as string
        ) &&
        !popperElement.current?.contains(target) &&
        !buttonElement.current?.contains(target) &&
        showPopup
      ) {
        setShowPopup(false);
        setIsPopupUpdated(false);
      }
    },
    [overflowMenuOptions, showPopup]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleClickOutside]);
  const uuid = new URLSearchParams(window.location.search).get('uuid');

  useEffect(() => {
    if (isPopupOpen) {
      setShowPopup(true);
    } else {
      setShowPopup(false);
    }
  }, [isPopupOpen, uuid]);

  useEffect(() => {
    if (showPopup && UpdatePopup) {
      UpdatePopup();
      setIsPopupUpdated(true);
    }
  }, [showPopup, UpdatePopup]);

  useEffect(() => {
    if (
      prevCommentsLength.current !== comments.length &&
      showPopup &&
      UpdatePopup
    ) {
      UpdatePopup();
    }
    prevCommentsLength.current = comments.length;
  }, [comments, showPopup, UpdatePopup]);

  const handleEscape = (event: KeyboardEvent) => {
    const key = event.key;
    if (key === 'Escape' || key === 'Esc') {
      setShowPopup(false);
      setIsPopupUpdated(false);
    }
  };

  return (
    <div className={className}>
      <div
        ref={buttonElement}
        className={classnames('relative', 'cursor-pointer')}
        onClick={() => setShowPopup((isOpen) => !isOpen)}
        data-testid={`${testId}-comments_icon-button`}
      >
        <IconButton
          name='chatbox'
          description='comment'
          hasASize={false}
          className={`${showPopup ? 'text-neutral-black' : 'text-neutral'}`}
        />

        <span
          className={classnames(
            'text-neutral-white',
            'absolute',
            'left-6px',
            'bottom-1',
            'text-8',
            'leading-3'
          )}
          data-testid='comment-button'
        >
          {comments.length <= 9 ? comments.length : '9+'}
        </span>
      </div>
      <div
        ref={popperElement}
        style={styles.popper}
        {...attributes.popper}
        className={classnames(
          'w-96',
          { invisible: !isPopupUpdated },
          'z-10',
          'bg-neutral-white',
          'rounded',
          'text-neutral-black',
          popperElevation
        )}
        hidden={!showPopup}
        data-testid={`${testId}-comments_list`}
      >
        <CommentsList
          comments={comments}
          onDelete={onDelete}
          onEdit={onEdit}
          emptyListMessage={intl.get('PROJECT_DETAIL.NO_COMMENTS')}
          mentionableUsers={mentionableUsers}
          mentionsFieldClassName='intake-comments-mentions'
          updateCommentsPopup={UpdatePopup}
        />
        <NewComment
          onCreate={onCreate}
          setOpen={setShowPopup}
          isOpen={showPopup}
          submitButtonLabel={intl.get('COMMENT.POST')}
          mentionableUsers={mentionableUsers}
          mentionsFieldClassName='intake-comments-mentions'
          updateCommentsPopup={UpdatePopup}
          placeholder={
            comments.length > 0
              ? intl.get('COMMENT.REPLY')
              : intl.get('COMMENT.WRITE_COMMENT')
          }
        />
      </div>
    </div>
  );
};

export default Comments;
